<script lang="ts">
import {defineComponent} from 'vue';
import AllSpiceDiffView from './AllSpiceDiffView.vue';
import ComponentLibrary from '../allspice_render/ComponentLibrary.vue';
import {fileExtension} from '../../file_utils.ts';

// If a design >100MB, don't render it by default.
export const FRONTEND_RENDER_THRESHOLD = 100 * 1024 * 1024;

export default defineComponent({
  name: 'AllSpiceDiff',

  components: {
    'allspice-diff-view': AllSpiceDiffView,
    ComponentLibrary,
  },

  props: {
    isDiff: {
      type: Boolean,
      required: false,
      default: true,
    },
    baseRepoOwner: {
      type: String,
      required: false,
      default: '',
    },
    baseRepoName: {
      type: String,
      required: false,
      default: '',
    },
    baseCommit: {
      type: String,
      required: false,
      default: '',
    },
    baseTreePath: {
      type: String,
      required: false,
      default: '',
    },
    headRepoOwner: {
      type: String,
      required: true,
      default: '',
    },
    headRepoName: {
      type: String,
      required: true,
      default: '',
    },
    headCommit: {
      type: String,
      required: true,
      default: '',
    },
    headTreePath: {
      type: String,
      required: true,
      default: '',
    },
    headSize: {
      type: Number,
      required: false,
      default: 0,
    },
    // Value is either three dots or two dots.
    diffType: {
      type: String,
      required: false,
      default: '...',
    },
    // Alt text for accessibility.
    alt: {
      type: String,
      required: false,
      default: '',
    },
    // The SVG class(es): as-snippet, as-thumbnail.
    svgClassNames: {
      type: String,
      required: false,
      default: '',
    },
    dataAsViewCoords: {
      type: String,
      required: false,
      default: '',
    },
    // Width-to-height aspect ratio.
    dataAsAspectRatio: {
      type: String,
      required: false,
      default: '',
    },
    // When true and loading the image errors, displays a default image.
    displayDefaultOnError: {
      type: Boolean,
      required: false,
      default: true,
    },
    colorProfile: {
      type: Object,
      required: false,
      default: null,
    },
    folded: {
      type: Boolean,
      required: false,
      default: false,
    },
    ignoreSizeCheck: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  /**
   * @returns {Data}
   */
  data() {
    return {
      shouldLoad: !this.isDiff || this.ignoreSizeCheck || this.headSize <= FRONTEND_RENDER_THRESHOLD,
    };
  },

  computed: {
    topLevelClassNames() {
      const svgClasses = (this.svgClassNames || '').split(' ');
      const names = ['as-diff-outer'];
      if (svgClasses.includes('as-thumbnail')) {
        names.push('thumbnail-outer');
      }
      return names;
    },

    simpleTreePath() {
      // Prefer head.
      return this.headTreePath || this.baseTreePath;
    },

    fileType() {
      const schematicExtensions = ['cpm', 'dsn', 'sdax', 'kicad_sch', 'prj'];

      const ext = fileExtension(this.simpleTreePath).toLowerCase();
      return schematicExtensions.includes(ext) ? 'schematic' : 'library';
    },

    isMultiDocument() {
      const multiDocumentExtensions = [
        'pcblib', 'schlib',
        'cpm', 'dsn', 'sdax',
        'kicad_sch', 'kicad_sym', 'prj',
      ];

      const ext = fileExtension(this.simpleTreePath).toLowerCase();
      return multiDocumentExtensions.includes(ext);
    },

    isSingleDocument() {
      return !this.isMultiDocument;
    },

    renderParams() {
      return {
        isDiff: this.isDiff,
        baseRepoOwner: this.baseRepoOwner,
        baseRepoName: this.baseRepoName,
        baseCommit: this.baseCommit,
        baseTreePath: this.baseTreePath,
        headRepoOwner: this.headRepoOwner,
        headRepoName: this.headRepoName,
        headCommit: this.headCommit,
        headTreePath: this.headTreePath,
        headSize: this.headSize,
        diffType: this.diffType,
        alt: this.alt,
        svgClassNames: this.svgClassNames,
        dataAsViewCoords: this.dataAsViewCoords,
        dataAsAspectRatio: this.dataAsAspectRatio,
        displayDefaultOnError: this.displayDefaultOnError,
        colorProfile: this.colorProfile,
      };
    },
  },

  methods: {
    load() {
      this.shouldLoad = true;
    },
  },
});
</script>
<template>
  <div :class="topLevelClassNames">
    <allspice-diff-view
      v-if="shouldLoad && isSingleDocument"
      :is-diff="isDiff"
      :base-repo-owner="baseRepoOwner"
      :base-repo-name="baseRepoName"
      :base-commit="baseCommit"
      :base-tree-path="baseTreePath"
      :head-repo-owner="headRepoOwner"
      :head-repo-name="headRepoName"
      :head-commit="headCommit"
      :head-tree-path="headTreePath"
      :diff-type="diffType"
      :alt="alt"
      :svg-class-names="svgClassNames"
      :data-as-view-coords="dataAsViewCoords"
      :data-as-aspect-ratio="dataAsAspectRatio"
      :display-default-on-error="displayDefaultOnError"
      :color-profile="colorProfile"
      :folded="folded"
    />
    <component-library
      v-if="shouldLoad && isMultiDocument"
      :is-diff="isDiff"
      :base-repo-owner="baseRepoOwner"
      :base-repo-name="baseRepoName"
      :base-commit="baseCommit"
      :base-tree-path="baseTreePath"
      :head-repo-owner="headRepoOwner"
      :head-repo-name="headRepoName"
      :head-commit="headCommit"
      :head-tree-path="headTreePath"
      :diff-type="diffType"
      :file-type="fileType"
      :render-params="renderParams"
      :color-profile="colorProfile"
      :folded="folded"
    />
    <div
      v-if="!shouldLoad"
      class="ui center aligned segment"
    >
      <p class="ui large text">
        Large diffs are not rendered by default.
      </p>
      <button
        class="ui primary button"
        @click="load"
      >
        Load Diff
      </button>
    </div>
  </div>
</template>
